import React, { useState } from 'react';
import { toast } from '../../lib/toast';
import { MIcon } from '../../lib/micon';
import { datumTTMMJJJJ } from '../../lib/datum';
import { datumLang } from '../../components/ld/LdFenster';
import '../../components/diktat/diktat-review.css';
import './ausflug.css';
import { ausflugApi, AusflugTermin, AusflugTeilnehmer } from './ausflug-api';

function defaultAnwesend(t: AusflugTeilnehmer): boolean {
  if (t.anwesend === 1) return true;
  if (t.anwesend === 0) return false;
  // Noch nicht erfasst: zugestimmt + (bezahlt|befreit) → default da
  return t.zustimmung === 'zugestimmt' && (t.zahlung === 'bezahlt' || t.zahlung === 'befreit');
}

const STATUS_LABEL: Record<string, string> = {
  zugestimmt: 'zugestimmt', abgelehnt: 'abgelehnt', offen: 'offen',
};

export function AnwesenheitErfassen({ termin, teilnehmer, onBack, onDone, bestaetigtVorjahr = false }: {
  termin: AusflugTermin;
  teilnehmer: AusflugTeilnehmer[];
  bestaetigtVorjahr?: boolean;   // Welle U5: Warnung "voriges Schuljahr" wurde bestätigt
  onBack: () => void;
  onDone: () => void;
}) {
  const [anwesend, setAnwesend] = useState<Set<number>>(
    () => new Set(teilnehmer.filter(defaultAnwesend).map(t => t.schueler_id))
  );
  const [speichern, setSpeichern] = useState(false);

  function toggle(sid: number) {
    setAnwesend(prev => { const n = new Set(prev); if (n.has(sid)) n.delete(sid); else n.add(sid); return n; });
  }

  async function speichereAnwesenheit() {
    setSpeichern(true);
    // Nur geänderte / noch nicht erfasste Teilnehmer patchen
    const toPatch = teilnehmer.filter(t => {
      const neu = anwesend.has(t.schueler_id) ? 1 : 0;
      return t.anwesend !== neu;
    });
    let neueFehlzeiten = 0;
    try {
      for (const t of toPatch) {
        const neu = anwesend.has(t.schueler_id) ? 1 : 0;
        const res = await ausflugApi.patchTeilnehmer(termin.id, t.schueler_id, { anwesend: neu as 0 | 1, ...(bestaetigtVorjahr ? { bestaetigt_vorjahr: true } : {}) });
        if (neu === 0 && res.fehlzeit_id && t.fehlzeit_id == null) neueFehlzeiten++;
      }
      toast(
        neueFehlzeiten > 0
          ? `Anwesenheit erfasst · ${neueFehlzeiten} Fehlzeit${neueFehlzeiten === 1 ? '' : 'en'} erstellt`
          : 'Anwesenheit erfasst',
        'success'
      );
      onDone();
    } catch (err: any) {
      toast(err.message ?? 'Speichern fehlgeschlagen.', 'error');
    } finally {
      setSpeichern(false);
    }
  }

  const daCount = anwesend.size;
  const fehltCount = teilnehmer.length - daCount;

  return (
    <div className="ld-page-enter ld-af">
      <button type="button" className="ld-kl-link ld-af__zurueck" onClick={onBack}><MIcon name="arrow_back" size={18} />Zurück zum Ausflug</button>
      <h1 className="ld-af__titel">Anwesenheit erfassen</h1>
      <p className="ld-af__kontext">{termin.titel}, {datumLang(termin.datum)}</p>

      <div className="ld-af-aw__leiste">
        <button type="button" className="ld-dr-knopf" onClick={() => setAnwesend(new Set(teilnehmer.map(t => t.schueler_id)))}>Alle da</button>
        <button type="button" className="ld-dr-knopf" onClick={() => setAnwesend(new Set())}>Keiner da</button>
        <span>{daCount} da, {fehltCount} {fehltCount === 1 ? 'fehlt' : 'fehlen'}</span>
      </div>

      <div className="ld-kl-karte ld-af-aw__liste">
        {teilnehmer.map(t => {
          const da = anwesend.has(t.schueler_id);
          return (
            <label key={t.id} className={da ? undefined : 'ist-aus'}>
              <input type="checkbox" checked={da} onChange={() => toggle(t.schueler_id)} />
              <span>{t.nachname}, {t.vorname}</span>
              <small>{STATUS_LABEL[t.zustimmung]}</small>
            </label>
          );
        })}
      </div>

      <p className="ld-af-aw__hinweis">Wer nicht da war, bekommt automatisch eine unentschuldigte Fehlzeit für den {datumTTMMJJJJ(termin.datum)}.</p>

      <div className="ld-af-aw__fuss">
        <button type="button" className="ld-fe-knopf ld-fe-knopf--zweit" onClick={onBack}>Abbrechen</button>
        <button type="button" className="ld-fe-knopf ld-fe-knopf--haupt" onClick={speichereAnwesenheit} disabled={speichern}>
          {speichern ? 'Bitte warten …' : 'Anwesenheit speichern'}
        </button>
      </div>
    </div>
  );
}
